import React, { useState } from 'react';
import { Modal, Form, InputNumber, Button, Alert } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import './RefundModal.css';

/**
 * RefundModal - Modal for processing transaction refunds
 * Supports full and partial refunds for Forte transactions
 * 
 * @param {boolean} visible - Modal visibility
 * @param {function} onClose - Close modal callback
 * @param {function} onRefund - Refund submit callback (amount)
 * @param {object} transaction - Transaction object {amount, registration_name, etc.}
 * @param {boolean} loading - Loading state during API call
 * @param {string} error - Error message to display
 */
const RefundModal = ({ visible, onClose, onRefund, transaction, loading, error }) => {
  const [form] = Form.useForm();
  const [refundAmount, setRefundAmount] = useState(null);

  const originalAmount = parseFloat(transaction?.amount || 0);

  const handleSubmit = () => {
    form.validateFields()
      .then(values => {
        onRefund(values.amount);
      })
      .catch(info => {
        console.log('Validation Failed:', info);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setRefundAmount(null);
    onClose();
  };

  const handleAmountChange = (value) => {
    setRefundAmount(value);
  };

  return (
    <Modal
      title={
        <span>
          <DollarOutlined style={{ marginRight: 8, color: '#ff7a00' }} />
          Process Refund
        </span>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit} 
          loading={loading}
          danger
        >
          Process Refund
        </Button>,
      ]}
      width={500}
    >
      <div className="refund-modal-content">
        {error && (
          <Alert
            message="Refund Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div className="refund-transaction-info">
          <p><strong>Registration:</strong> {transaction?.registration_name || 'N/A'}</p>
          <p><strong>Customer:</strong> {transaction?.customer_name || 'N/A'}</p>
          <p><strong>Original Amount:</strong> ${originalAmount.toFixed(2)}</p>
          <p><strong>Transaction ID:</strong> {transaction?.transaction_id || 'N/A'}</p>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ amount: originalAmount }}
        >
          <Form.Item
            label="Refund Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter refund amount' },
              {
                type: 'number',
                min: 0.01,
                max: originalAmount,
                message: `Amount must be between $0.01 and $${originalAmount.toFixed(2)}`,
              },
            ]}
            extra={`Maximum refund: $${originalAmount.toFixed(2)}`}
          >
            <InputNumber
              prefix="$"
              placeholder="0.00"
              style={{ width: '100%' }}
              precision={2}
              step={0.01}
              min={0.01}
              max={originalAmount}
              onChange={handleAmountChange}
            />
          </Form.Item>
        </Form>

        {refundAmount && refundAmount < originalAmount && (
          <Alert
            message="Partial Refund"
            description={`You are processing a partial refund of $${refundAmount.toFixed(2)}. The remaining balance will be $${(originalAmount - refundAmount).toFixed(2)}.`}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        <Alert
          message="Important"
          description="This action cannot be undone. The refund will be processed immediately to the customer's original payment method."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </div>
    </Modal>
  );
};

export default RefundModal;
